import { createContext, useContext, useEffect, useState, ReactNode } from "react"
import { setViewerTimezone } from "@/lib/task-due"
import axios from "axios"
import { apiClient, setUnauthenticatedHandler } from "@/lib/api"
import { workflowApi } from "@/services/api/workflow.api"
import { SessionExpiredDialog } from "@/components/session-expired-dialog"
import type { Role } from "@/lib/roles"

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api"
const ACTIVE_LICENSE_KEY = "activeLicenseId"
const AUTH_CACHE_KEY = "forge-auth-identity"
// Must match the persister key in main.tsx.
const QUERY_CACHE_KEY = "forge-query-cache"

interface License {
  id: number
  key: string
  client_name: string
  domain: string
  site_id: number | null // The actual site ID to use for API calls
  // 'plugin' unlocks plugin-gated features (Yoast/Rank Math SEO sync, CTAs).
  // 'rest_api' (app_password) blocks them. null = no connection configured yet.
  wordpress_connection_method: 'plugin' | 'rest_api' | null
}

interface User {
  id: number
  name: string
  email: string
  avatar: string | null
  /** owner > admin > user. Ask `isAdmin(user)` (@/lib/roles), never compare the string: an owner is an admin too. */
  role: Role
  /** True for an owner as well as an admin. */
  is_admin: boolean
  /** The owner (Justin): the only one who can grant or remove owner, and who always has Events. */
  is_owner?: boolean
  /**
   * The admin pages this person may open (API AdminPermissions): every key
   * for the owner, the granted ones for an admin, [] otherwise. Ask
   * hasAdminPermission (@/lib/workspaces) rather than reading it directly.
   */
  admin_permissions?: string[]
  /** The admin keys only the owner holds (API AdminPermissions owner_only). */
  owner_only_admin_permissions?: string[]
  /** May mint personal API tokens: admins always, others when an admin enabled it. */
  api_access_enabled?: boolean
  /** May read the team prompt log (Prompts): the owner, admins holding the prompt_log permission, others the owner enabled. */
  prompt_log_enabled?: boolean
  /** Sees Outreach (the cold-email queue). Flag-only: admins need it granted too. */
  outreach_enabled?: boolean
  /** Events access as /auth/user reports it: the owner, or anyone (admins included) with the per-person toggle. */
  events_enabled?: boolean
  /** A Forge Support project member (not the owner): Support shows their projects only. */
  support_member?: boolean
  /** Has submitted an invoice or is an invoice participant; false hides My invoices from the sidebar. */
  has_invoices?: boolean
  /** null = Standard access; a list = the only workspaces this person has (Admin → Users). */
  workspaces?: string[] | null
  /** Outside the agency (Admin → Users → External): their Forge is their own boards' tasks, nothing else. */
  is_external?: boolean
  last_login_at: string | null
  /** Per-person UI choices that follow them across devices. */
  ui_preferences?: UiPreferences
  /** FOR-302: IANA zone due times are picked and shown in (the API defaults it to America/New_York). */
  timezone?: string
}

export interface UiPreferences {
  /** Desktop sidebar: labels beside the icons, or the icon rail. Unset reads as full. */
  sidebar_style?: "full" | "icons"
}

interface AuthContextType {
  user: User | null
  licenses: License[]
  currentLicense: License | null
  isLoading: boolean
  isAuthenticated: boolean
  /** FOR-194: true while a 401 has the session-expired dialog up. Editors
      use it to flush their local draft before anything else can happen. */
  sessionExpired: boolean
  logout: () => Promise<void>
  refresh: () => Promise<void>
  /** Applies immediately, then saves; a failed save puts the old value back. */
  updateUiPreferences: (patch: Partial<UiPreferences>) => Promise<void>
  /** Saves the profile timezone (null = back to the default, America/New_York). */
  updateTimezone: (timezone: string | null) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface CachedIdentity {
  user: User
  licenses: License[]
}

/**
 * The last identity the server confirmed.
 *
 * This is a rendering hint and never an authorisation decision: every request
 * still carries the session cookie and the server still refuses the ones it
 * should. What it buys is the first paint — without it, every navigation to
 * the app blocks on /auth/user behind a full-screen loader, which also throws
 * away whatever the persisted query cache could have painted immediately.
 *
 * fetchUser below clears it the moment the server disagrees.
 */
function readCachedIdentity(): CachedIdentity | null {
  try {
    const raw = localStorage.getItem(AUTH_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // A cache written by an older shape is worth no more than no cache.
    if (!parsed?.user?.id) return null
    return { user: parsed.user, licenses: parsed.licenses ?? [] }
  } catch {
    return null
  }
}

function writeCachedIdentity(user: User, licenses: License[]) {
  try {
    localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify({ user, licenses }))
  } catch {
    // A full or unavailable localStorage costs a fast first paint, nothing more.
  }
}

function clearCachedIdentity() {
  try {
    localStorage.removeItem(AUTH_CACHE_KEY)
  } catch {
    // Same: losing the cache is the safe direction to fail in.
  }
}

/**
 * Chooses the active licence and installs its headers on both API clients.
 *
 * Extracted so hydration can run it synchronously, before any child renders.
 * A query that fires before these headers exist is answered for the wrong
 * site, so this cannot wait for the /auth/user round trip.
 */
function activateLicense(userLicenses: License[]): License | null {
  if (userLicenses.length === 0) return null

  // Deep link from an email (the weekly client pulse links /leads?license=13):
  // switch to that client when this person holds it, then drop the param so a
  // later reload or shared URL does not keep forcing it.
  const params = new URLSearchParams(window.location.search)
  const linked = params.get("license")
  if (linked) {
    params.delete("license")
    const query = params.toString()
    const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
    if (userLicenses.some((l) => l.id === parseInt(linked)) && localStorage.getItem(ACTIVE_LICENSE_KEY) !== linked) {
      // Same as a sidebar switch: swap the license, drop the outgoing client's
      // cached responses and reload, so nothing already in memory leaks across.
      localStorage.setItem(ACTIVE_LICENSE_KEY, linked)
      localStorage.removeItem("forge-query-cache")
      window.location.replace(cleanUrl)
    } else {
      window.history.replaceState(window.history.state, "", cleanUrl)
    }
  }

  const storedLicenseId = localStorage.getItem(ACTIVE_LICENSE_KEY)
  let activeLicense: License | undefined

  if (storedLicenseId) {
    // Try to use stored license if user still has access
    activeLicense = userLicenses.find((l) => l.id === parseInt(storedLicenseId))
  }

  // Fall back to first license if stored one not found
  if (!activeLicense) {
    activeLicense = userLicenses[0]
    localStorage.setItem(ACTIVE_LICENSE_KEY, activeLicense.id.toString())
  }

  apiClient.setActiveLicense(activeLicense.id, activeLicense.domain)
  workflowApi.setActiveLicense(activeLicense.id, activeLicense.domain)
  return activeLicense
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [cached] = useState(readCachedIdentity)
  const [user, setUser] = useState<User | null>(cached?.user ?? null)
  // Before the children render, so every due time on the page reads in it.
  setViewerTimezone(user?.timezone)
  const [licenses, setLicenses] = useState<License[]>(cached?.licenses ?? [])
  const [currentLicense, setCurrentLicense] = useState<License | null>(() =>
    cached ? activateLicense(cached.licenses) : null
  )
  // Only a first-time or logged-out visitor waits behind the loader. Everyone
  // else renders from cache while the fetch below revalidates in the background.
  const [isLoading, setIsLoading] = useState(cached === null)
  const [sessionExpired, setSessionExpired] = useState(false)

  // FOR-194: a 401 from any request — including a background poll firing
  // while someone is mid-paragraph — used to hard-navigate to /login and
  // destroy the page. Show the re-auth dialog over the living app instead.
  // Deliberately no clearCachedIdentity()/query-cache clear here: the page
  // must stay intact so unsaved work survives the re-login.
  useEffect(() => {
    setUnauthenticatedHandler(() => setSessionExpired(true))
    return () => setUnauthenticatedHandler(null)
  }, [])

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/user`, {
        withCredentials: true,
      })

      if (response.data.authenticated) {
        setUser(response.data.user)
        const userLicenses = response.data.licenses || []
        setLicenses(userLicenses)

        // CRITICAL: Set active license headers immediately after fetching user
        if (userLicenses.length > 0) {
          setCurrentLicense(activateLicense(userLicenses))
        }

        writeCachedIdentity(response.data.user, userLicenses)
      } else {
        setUser(null)
        setLicenses([])
        setCurrentLicense(null)
        apiClient.clearActiveLicense()
        workflowApi.clearActiveLicense()
        clearCachedIdentity()
      }
    } catch (error: any) {
      console.error("Failed to fetch user:", error)

      // If the user's account was deactivated, redirect to login with a clear message
      if (error?.response?.status === 403 && error?.response?.data?.deactivated) {
        setUser(null)
        setLicenses([])
        setCurrentLicense(null)
        apiClient.clearActiveLicense()
        workflowApi.clearActiveLicense()
        localStorage.removeItem(ACTIVE_LICENSE_KEY)
        clearCachedIdentity()
        window.location.href = "/login?error=deactivated"
        return
      }

      // Every other failure lands here, including an expired session and an
      // unreachable API. Both already drop the user to /login, so the cache has
      // to go with them — keeping it would hydrate an identity on the next load
      // that the server has already refused.
      setUser(null)
      setLicenses([])
      apiClient.clearActiveLicense()
      workflowApi.clearActiveLicense()
      clearCachedIdentity()
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchUser()
  }, [])

  const logout = async () => {
    try {
      await axios.post(
        `${API_URL}/auth/logout`,
        {},
        {
          withCredentials: true,
        }
      )
    } catch (error) {
      console.error("Logout error:", error)
    } finally {
      setUser(null)
      setLicenses([])
      setCurrentLicense(null)
      apiClient.clearActiveLicense()
      localStorage.removeItem(ACTIVE_LICENSE_KEY)
      clearCachedIdentity()
      // The persisted query cache outlives the session otherwise, so the next
      // person to sign in on a shared machine gets the previous user's
      // dashboard painted from localStorage before their own data arrives.
      localStorage.removeItem(QUERY_CACHE_KEY)
      window.location.href = "/login"
    }
  }

  const refresh = async () => {
    await fetchUser()
  }

  const updateUiPreferences = async (patch: Partial<UiPreferences>) => {
    if (!user) return
    const previous = user.ui_preferences ?? {}
    const optimistic = { ...user, ui_preferences: { ...previous, ...patch } }
    setUser(optimistic)
    writeCachedIdentity(optimistic, licenses)
    try {
      const saved = await apiClient.patch<{ ui_preferences: UiPreferences }>("/auth/user/preferences", patch)
      setUser((current) => {
        if (!current) return current
        const next = { ...current, ui_preferences: saved.ui_preferences }
        writeCachedIdentity(next, licenses)
        return next
      })
    } catch (error) {
      console.error("Failed to save UI preferences:", error)
      setUser((current) => (current ? { ...current, ui_preferences: previous } : current))
      writeCachedIdentity({ ...user, ui_preferences: previous }, licenses)
      throw error
    }
  }

  const updateTimezone = async (timezone: string | null) => {
    if (!user) return
    const saved = await apiClient.patch<{ timezone: string }>("/auth/user/preferences", { timezone })
    setUser((current) => {
      if (!current) return current
      const next = { ...current, timezone: saved.timezone }
      writeCachedIdentity(next, licenses)
      return next
    })
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        licenses,
        currentLicense,
        isLoading,
        isAuthenticated: user !== null,
        sessionExpired,
        logout,
        refresh,
        updateUiPreferences,
        updateTimezone,
      }}
    >
      {children}
      <SessionExpiredDialog
        open={sessionExpired}
        onSignedIn={async () => {
          await fetchUser()
          setSessionExpired(false)
        }}
      />
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
